import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import BookingNoteField from "@/components/tpr/BookingNoteField/BookingNoteField";
import { defaultButtonClasses, secondaryButtonClasses } from "@/constants/ui";
import { RECURRENCE_OPTIONS, type RecurrencePatternOption } from "@/types/Recurrence";
import { AlertCircle, AlertTriangle, Clock, Info } from "lucide-react";
import { BookingSummary } from "../components/BookingSummary";
import { BookingTypeToggle } from "../components/BookingTypeToggle";
import { DurationRocker } from "../components/DurationRocker";
import { MemberSelector } from "../components/MemberSelector";
import { RecurringConflictList } from "../components/RecurringConflictList";
import { useMakeABookingDialog } from "../useMakeABookingDialog";
import type { BookingType } from "../types";

const SLOT_DEFAULT_DURATION_MINUTES = 180; // 3 hours default for slot bookings

export const BookScreen = () => {
  const {
    formData,
    updateFormData,
    isElevatedUser,
    isSubmitting,
    error,
    isCheckingAvailability,
    isTimeSlotAvailable,
    availabilityError,
    recurringConflictDates,
    bookingGaps,
    multiRoomConflicts,
    isPastDateWarning,
    pastOccurrenceCount,
    requiresStaffNote,
    serverWarnings,
    submitBooking,
    navigateTo,
    onClose,
  } = useMakeABookingDialog();

  const formatHHMM = (hhmm: string) => hhmm.replace(/^(\d{2})(\d{2})$/, "$1:$2");

  // A booking note is mandatory in two cases: (1) an elevated user booking for THEMSELVES
  // (requiresStaffNote), and (2) waiving billing on an elevated booking — a FOC booking must
  // record WHY, so the reason surfaces on the Charges Waived report. Either blocks the "Book"/"Next"
  // CTA until a note is entered.
  const noteRequired = requiresStaffNote || (isElevatedUser && !!formData.waiveBilling);
  const staffNoteMissing = noteRequired && !formData.staffNote.trim();

  // Determine CTA label and action
  const ctaConfig = useMemo(() => {
    const needsSecondScreen = formData.bookingType === "slot" || formData.isRecurring;
    return {
      label: needsSecondScreen ? "Next" : "Book",
      // Wrapped (not `submitBooking` directly) — the Button's onClick hands this the click
      // MouseEvent as its first argument. Passing that straight through to submitBooking would land
      // in its `overrideBufferConflict` parameter (any object is truthy), silently sending every
      // normal submit as an override. Call submitBooking with no arguments explicitly.
      action: needsSecondScreen ? () => navigateTo("repeat-setup") : () => void submitBooking(),
    };
  }, [formData.bookingType, formData.isRecurring, navigateTo, submitBooking]);

  const handleDurationChange = (minutes: number) => {
    updateFormData({ durationMinutes: minutes });
  };

  // The presets carry a default `occurrencesToCheck` (e.g. Weekly = 52) that other flows use for
  // availability checks, but for this flow it drives the OccurrenceCountPicker default: a defined
  // count selects "A set number of times", undefined selects "No end date". We want "No end date"
  // as the default, so strip the preset's count when seeding the pattern — the operator opts into a
  // fixed count via the picker.
  const withoutCount = ({ occurrencesToCheck: _omit, ...rest }: RecurrencePatternOption) => rest;

  const handleRecurringChange = (checked: boolean) => {
    updateFormData({
      isRecurring: checked,
      recurrencePattern: checked ? withoutCount(RECURRENCE_OPTIONS[0]) : null,
    });
  };

  const handleGroupBookingChange = (checked: boolean) => {
    updateFormData({ isGroupBooking: checked });
  };

  const handleRecurrencePatternChange = (label: string) => {
    const pattern = RECURRENCE_OPTIONS.find((opt) => opt.label === label);
    if (pattern) {
      // Preserve the operator's current count choice across a frequency change rather than
      // resetting it to the preset's default.
      updateFormData({
        recurrencePattern: { ...withoutCount(pattern), occurrencesToCheck: formData.recurrencePattern?.occurrencesToCheck },
      });
    }
  };

  const handleBookingTypeChange = (type: BookingType) => {
    // If switching to slot, default to 3 hours (but don't enforce a minimum)
    const newDuration = type === "slot" ? SLOT_DEFAULT_DURATION_MINUTES : formData.durationMinutes;

    updateFormData({
      bookingType: type,
      durationMinutes: newDuration,
      // If switching to slot, recurring is implicit (hide the toggle)
      isRecurring: type === "slot" ? false : formData.isRecurring,
      recurrencePattern: type === "slot" ? null : formData.recurrencePattern,
    });
  };

  const handleClose = () => {
    onClose?.();
  };

  const renderFormContent = () => (
    <div className="space-y-6">
      {/* Error display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-800 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-800">Booking could not be created</p>
              <p className="text-sm text-red-800 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Non-blocking server warnings from /can-book — e.g. the termination-waitlist notice when
          booking for a member whose membership is ending. Shown as amber info; booking still allowed. */}
      {serverWarnings.map((warning, idx) => (
        <div
          key={`server-warning-${idx}`}
          data-testid="server-warning"
          className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg"
        >
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 flex-1">{warning}</p>
        </div>
      ))}

      {/* R2b — past-date warning banner (elevated users only, past in-window date) */}
      {isPastDateWarning && isElevatedUser && (
        <div
          data-testid="past-date-warning"
          className="bg-amber-50 border border-amber-300 rounded-lg p-4"
        >
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-amber-800 text-sm">This date is in the past</p>
              <p className="text-sm text-amber-700 mt-1">
                {pastOccurrenceCount} past occurrence{pastOccurrenceCount !== 1 ? "s" : ""} will be created and billed.
              </p>
              <div className="flex items-center gap-2 mt-3">
                <Checkbox
                  id="waive-billing-checkbox"
                  data-testid="waive-billing-checkbox"
                  checked={formData.waiveBilling}
                  onCheckedChange={(checked) => updateFormData({ waiveBilling: checked === true })}
                  disabled={isSubmitting}
                />
                <Label htmlFor="waive-billing-checkbox" className="text-sm text-amber-800 cursor-pointer">
                  Waive billing for this booking
                </Label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Member Selector */}
      <div className="mb-8">
        <MemberSelector />
      </div>

      {/* Booking note — saved as a booking comment (surfaces on the Flex report + staff view, and to
          the member on their own view when they authored it). Optional for member / admin-self
          bookings (collapsible), MANDATORY when an elevated user books on behalf of another member. */}
      <div className="mb-8">
        <BookingNoteField
          value={formData.staffNote}
          onChange={(value) => updateFormData({ staffNote: value })}
          required={noteRequired}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 bg-gray-100 shadow p-4 rounded-lg">
        <div>
          {/* Duration Rocker */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">How long do you need the room for?</Label>
            <DurationRocker
              value={formData.durationMinutes}
              onChange={handleDurationChange}
              disabled={isSubmitting || isCheckingAvailability}
            />
            {/* Availability error display. For recurring conflicts we list the specific dates
                (each expandable to a card) instead of the generic one-line message. */}
            {recurringConflictDates.length > 0 ? (
              <RecurringConflictList conflicts={recurringConflictDates} proposedTime={formData.time} />
            ) : (
              availabilityError && (
                <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                  <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800">{availabilityError}</p>
                </div>
              )
            )}
          </div>
        </div>
        <div>
          {/* Booking Type Toggle - Only for elevated users */}
          {isElevatedUser && (
            <BookingTypeToggle
              value={formData.bookingType}
              onChange={handleBookingTypeChange}
              disabled={isSubmitting}
            />
          )}
        </div>
      </div>

      {/* Is Recurring Toggle - Hidden when booking type is "slot" */}
      {formData.bookingType !== "slot" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Make this recurring?</Label>
                <p className="text-xs text-gray-500 mt-0.5">Book the same time on a regular basis</p>
              </div>
              <Switch checked={formData.isRecurring} onCheckedChange={handleRecurringChange} disabled={isSubmitting} />
            </div>
          </div>
          <div>
            {/* Recurrence Pattern Selector - Only shown when recurring is enabled */}
            {formData.isRecurring && (
              <div className="pl-0">
                {/* <Label className="text-sm font-medium mb-2 block">Repeat</Label> */}
                <Select
                  value={formData.recurrencePattern?.label || RECURRENCE_OPTIONS[0].label}
                  onValueChange={handleRecurrencePatternChange}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    {RECURRENCE_OPTIONS.map((option) => (
                      <SelectItem key={option.label} value={option.label}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Group Booking Toggle — pure tag, applies to all booking types */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Group booking</Label>
              <p className="text-xs text-gray-500 mt-0.5">Tag this session for manual group-booking billing</p>
            </div>
            <Switch
              data-testid="group-booking-switch"
              checked={formData.isGroupBooking}
              onCheckedChange={handleGroupBookingChange}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>

      {/* Gap / continuous-booking warnings */}
      {bookingGaps.map((gap, idx) => {
        const isContinuous = gap.warningType === "continuous";
        const isOrgContinuous = isContinuous && gap.isSameOrg === true;
        // Chargeable warnings (same-member "continuous" bridging — the member IS billed for the gap)
        // are shown in red so they stand apart from informational amber reminders (e.g. mindfulness
        // prompts). The cross-member unbookable-gap warning is never billed, so it stays amber.
        return (
          <div
            key={`${gap.direction}-${idx}`}
            data-testid={isContinuous ? "continuous-booking-warning" : "unbookable-gap-warning"}
            className={
              isContinuous
                ? "flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg"
                : "flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg"
            }
          >
            <AlertTriangle
              className={`h-4 w-4 flex-shrink-0 ${isContinuous ? "text-red-600" : "text-amber-600"}`}
            />
            <p className={`text-xs flex-1 ${isContinuous ? "text-red-800" : "text-amber-800"}`}>
              {isOrgContinuous
                ? `This leaves a ${gap.gapRawMinutes}-min gap ${gap.direction} another booking owned by your organisation — because the organisation owns both, you'll be charged for the gap as one continuous block.`
                : isContinuous
                ? `This leaves an unbookable gap of ${gap.gapRawMinutes}-min — if you create this booking you will be charged for the gap.`
                : `Creates a ${gap.gapRawMinutes}-min gap ${gap.direction} your booking that no one else can use.`}
            </p>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={isContinuous ? "text-red-700 hover:text-red-900" : "text-amber-700 hover:text-amber-900"}
                >
                  <Info className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent side="left" className="w-72 text-sm">
                {isContinuous ? (
                  <>
                    <p className="font-medium mb-2">Continuous booking suggested</p>
                    <p className="text-gray-600 text-xs mb-2">
                      {isOrgContinuous
                        ? "The adjacent booking belongs to another member of your organisation. Because the organisation owns both, the gap between them is continuous room use."
                        : "The adjacent booking is yours. The gap between them isn't large enough to be booked by anyone else, including you."}
                    </p>
                    <p className="text-gray-600 text-xs">
                      {isOrgContinuous
                        ? "If you proceed, your organisation will be billed for the unusable gap as if the room was booked."
                        : "If you proceed, you will be billed for the unusable gap as if the room was booked."}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-medium mb-2">What is an unbookable gap?</p>
                    <p className="text-gray-600 text-xs mb-2">
                      A gap of up to 2 hours between bookings (including 15-min buffers) cannot fit another booking.
                    </p>
                    <p className="text-gray-600 text-xs">
                      This reduces room availability for other members. Consider adjusting your duration to close the
                      gap.
                    </p>
                  </>
                )}
              </PopoverContent>
            </Popover>
          </div>
        );
      })}

      {/* R22 — multi-room same-time warning (allowed, but the member pays for both). Non-blocking. */}
      {multiRoomConflicts.length > 0 && (
        <div
          data-testid="multi-room-warning"
          className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg"
        >
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
          <p className="text-xs text-amber-800 flex-1">
            You already have a booking in another room at this time (
            {multiRoomConflicts.map((c) => `${formatHHMM(c.startTime)}–${formatHHMM(c.endTime)}`).join(", ")}). You can
            book both, but you'll be charged for each.
          </p>
        </div>
      )}

      {/* CTAs */}
      <div className="flex flex-col gap-2 mt-6">
        <Button
          type="button"
          data-testid="make-booking-cta"
          className={`w-full ${defaultButtonClasses}`}
          onClick={ctaConfig.action}
          disabled={isSubmitting || isCheckingAvailability || !isTimeSlotAvailable || staffNoteMissing}
        >
          {isSubmitting
            ? "Creating booking..."
            : isCheckingAvailability
              ? "Checking availability..."
              : !isTimeSlotAvailable
                ? "Time Not Available"
                : staffNoteMissing
                  ? "Add a booking note"
                  : ctaConfig.label}
        </Button>
        <Button
          type="button"
          variant="outline"
          className={`w-full ${secondaryButtonClasses}`}
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Close
        </Button>
      </div>
    </div>
  );

  return (
    <div className="w-full flex flex-col bg-white rounded-xl my-4">
      {/* Header - Mobile only */}
      <div className="lg:hidden p-4 py-6 flex flex-col circle-pattern-background circle-pattern-indigo-50 rounded-t-2xl">
        <h2 className="text-2xl font-semibold mb-2">Create a Booking</h2>
        <p className="text-gray-600 text-xs">Review the details below and confirm your booking.</p>
        {/* Booking Summary for Mobile */}
        <div className="mt-4 p-3 bg-white/80 rounded-lg">
          <BookingSummary />
        </div>
      </div>

      {/* Desktop grid layout */}
      <div className="hidden lg:grid lg:grid-cols-3 grow gap-1">
        {/* Left column - Information */}
        <div className="p-6 lg:rounded-lg flex flex-col circle-pattern-background circle-pattern-indigo-50">
          <h2 className="text-3xl font-semibold mb-4">Create a Booking</h2>
          <p className="mb-4 text-gray-600 text-sm">
            Review the booking details and make any adjustments before confirming.
          </p>

          {/* Booking Summary */}
          <div className="mt-2 p-4 bg-white/80 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Booking Details</h3>
            <BookingSummary />
          </div>
        </div>

        {/* Right column - Form */}
        <div className="bg-gray-50 rounded-lg lg:rounded-r-xl lg:col-span-2 p-6 border-l border-gray-200 shadow-sm overflow-y-auto">
          {renderFormContent()}
        </div>
      </div>

      {/* Mobile content area */}
      <div className="lg:hidden flex-1 bg-gray-50 p-6">{renderFormContent()}</div>
    </div>
  );
};

export default BookScreen;
